"use client";

import { useEffect, type ComponentProps } from "react";
import { ConvexError } from "convex/values";
import { ErrorPageContent } from "@/components/feedback/error-page-content";
import { captureError } from "@/lib/error-tracking";
import { recoverFromRetiredDeployment } from "@/lib/error-tracking/deployment-recovery";
import {
  isBrowserNetworkFailure,
  networkFailureCaptureContext,
} from "@/lib/telemetry/browser-network-failure";
import { isExpectedApplicationError } from "@/lib/telemetry/expected-errors";

interface RouteErrorViewProps {
  error: Error & { digest?: string };
  reset: () => void;
  navigation: {
    label: string;
    href: string;
  };
  title?: string;
  description?: string;
  variant?: "centered" | "card";
  showStack?: boolean;
  reportTitlePrefix?: string;
  reportable?: boolean;
  containerClassName: string;
  icon?: ComponentProps<typeof ErrorPageContent>["icon"];
  skipErrorCapture?: boolean;
}

function useCaptureRouteError(
  error: Error & { digest?: string },
  navigation: RouteErrorViewProps["navigation"],
  skipErrorCapture: boolean,
) {
  useEffect(() => {
    if (error instanceof ConvexError || isExpectedApplicationError(error)) {
      return;
    }
    const recovery = recoverFromRetiredDeployment(error);
    if (recovery === "reloading" || skipErrorCapture) return;

    captureError(error, {
      ...networkFailureCaptureContext(error, "route-error-view"),
      tags: {
        surface: "route-error-view",
        ...(isBrowserNetworkFailure(error) && { browser_network_failure: true }),
        ...(recovery === "exhausted" && {
          chunk_load_recovery_exhausted: true,
        }),
        ...(recovery === "storage_unavailable" && {
          chunk_load_recovery_storage_unavailable: true,
        }),
      },
      extra: {
        digest: error.digest,
        navigationHref: navigation.href,
        navigationLabel: navigation.label,
      },
    });
  }, [error, navigation.href, navigation.label, skipErrorCapture]);
}

export function RouteErrorView({
  error,
  reset,
  navigation,
  title,
  description,
  variant = "centered",
  showStack = true,
  reportTitlePrefix,
  reportable,
  containerClassName,
  icon,
  skipErrorCapture = false,
}: RouteErrorViewProps) {
  useCaptureRouteError(error, navigation, skipErrorCapture);

  return (
    <div className={containerClassName}>
      <ErrorPageContent
        error={error}
        reset={reset}
        title={title}
        description={description}
        icon={icon}
        variant={variant}
        showStack={showStack}
        navigation={navigation}
        reportTitlePrefix={reportTitlePrefix}
        reportable={reportable}
      />
    </div>
  );
}
